import React, { useEffect, useMemo, useRef, useState } from 'react';
import plantillasContratoService from '../services/plantillasContratoService';
import { construirContenidoVistaPreviaPlantilla } from '../utils/plantillaContratoPreview';
import ContratoVistaPreviaPaginada from './ContratoVistaPreviaPaginada';
import { aplicarNegritaEnTextarea, normalizarMarcadoresNegrita } from '../utils/contratoTextoFormato';
import { obtenerLogoEmpresaParaVista } from '../services/logoEmpresaService';
import {
  FUENTES_CONTRATO,
  FUENTE_CONTRATO_DEFAULT,
  normalizarFuenteContrato,
  obtenerEtiquetaFuenteContrato,
  resolverStackFuenteContrato,
} from '../utils/contratoFuentes';
import {
  articuloVacio,
  articuloDesdeApi,
  articuloParaApi,
  articuloTieneContenido,
} from '../utils/articuloPlantillaContrato';

/** Quita un único envoltorio **...** del título (el <h3> ya lo pone en negrita). */
function tituloSinNegritaEnvolvente(texto) {
  const t = String(texto || '').trim();
  const m = t.match(/^\*\*([\s\S]+)\*\*$/);
  return m ? m[1].replace(/\s+/g, ' ').trim() : texto || '';
}

const INTRODUCCION_PLACEHOLDER =
  'Comparecen de una parte {{empresa.nombre_empresa}}, con RUC N° {{empresa.ruc}}, representada por {{empresa.representante_legal}}, identificada con DNI N° {{empresa.dni_representante}}, con domicilio en {{empresa.direccion}}, a quien en adelante se denominará EL EMPLEADOR; y de la otra parte {{empleado.nombre_completo}}, identificado(a) con {{empleado.tipo_documento}} N° {{empleado.numero_documento}}, a quien en adelante se denominará EL TRABAJADOR. Ambas partes acuerdan celebrar el presente contrato sujeto a las siguientes cláusulas:';

const formatearErrorPlantilla = (response) => {
  if (response?.errors && typeof response.errors === 'object') {
    const mensajes = Object.values(response.errors).flat().filter(Boolean);
    if (mensajes.length > 0) return mensajes.join(' ');
  }

  return response?.error || response?.message || 'No se pudo guardar la plantilla';
};

const PlantillaContratoModal = ({
  isOpen,
  onClose,
  empresaId,
  empresa = null,
  tiposContrato = [],
  plantilla = null,
  onSuccess,
}) => {
  const [tipo, setTipo] = useState('');
  const [titulo, setTitulo] = useState('');
  const [fuente, setFuente] = useState(FUENTE_CONTRATO_DEFAULT);
  const [fuentesDisponibles, setFuentesDisponibles] = useState(FUENTES_CONTRATO);
  const [introduccion, setIntroduccion] = useState('');
  const [articulos, setArticulos] = useState([articuloVacio()]);
  const [variablesDisponibles, setVariablesDisponibles] = useState([]);
  const [campoActivo, setCampoActivo] = useState('introduccion');
  const [articuloActivo, setArticuloActivo] = useState(0);
  const [parrafoActivo, setParrafoActivo] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [logoEmpresaUrl, setLogoEmpresaUrl] = useState(null);
  const introTextareaRef = useRef(null);
  const parrafoTextareaRefs = useRef({});

  useEffect(() => {
    if (!isOpen) return;

    const cargarVariables = async () => {
      const response = await plantillasContratoService.obtenerVariablesDisponibles();
      if (response.success) {
        setVariablesDisponibles(response.data);
        if (Array.isArray(response.fuentes) && response.fuentes.length > 0) {
          setFuentesDisponibles(response.fuentes);
        }
      }
    };

    cargarVariables();

    if (plantilla) {
      setTipo(plantilla.tipo || '');
      setTitulo(plantilla.titulo || '');
      setFuente(normalizarFuenteContrato(plantilla.fuente));
      setIntroduccion(normalizarMarcadoresNegrita(plantilla.introduccion || ''));
      setArticulos(
        Array.isArray(plantilla.articulos) && plantilla.articulos.length > 0
          ? plantilla.articulos.map((item) => {
              const art = articuloDesdeApi(item);
              return {
                titulo: tituloSinNegritaEnvolvente(normalizarMarcadoresNegrita(art.titulo || '')),
                parrafos: (art.parrafos || ['']).map((p) => normalizarMarcadoresNegrita(p)),
              };
            })
          : [articuloVacio()]
      );
    } else {
      setTipo(tiposContrato[0] || '');
      setTitulo('');
      setFuente(FUENTE_CONTRATO_DEFAULT);
      setIntroduccion('');
      setArticulos([articuloVacio()]);
    }

    setCampoActivo('introduccion');
    setArticuloActivo(0);
    setParrafoActivo(0);
    setError('');
  }, [isOpen, plantilla, tiposContrato]);

  useEffect(() => {
    if (!isOpen || !empresaId) {
      setLogoEmpresaUrl(null);
      return;
    }

    let activo = true;

    obtenerLogoEmpresaParaVista(empresaId).then((src) => {
      if (activo) setLogoEmpresaUrl(src);
    });

    return () => {
      activo = false;
    };
  }, [isOpen, empresaId]);

  const variablesPorGrupo = useMemo(() => {
    return variablesDisponibles.reduce((acc, variable) => {
      const grupo = variable.grupo || 'Otros';
      if (!acc[grupo]) acc[grupo] = [];
      acc[grupo].push(variable);
      return acc;
    }, {});
  }, [variablesDisponibles]);

  const htmlContenidoVistaPrevia = useMemo(
    () =>
      construirContenidoVistaPreviaPlantilla({
        tipo,
        titulo,
        introduccion,
        articulos,
        empresa,
        fuente,
      }),
    [tipo, titulo, introduccion, articulos, empresa, fuente]
  );

  const fontFamilyVistaPrevia = useMemo(
    () => resolverStackFuenteContrato(fuente),
    [fuente]
  );

  const actualizarArticulo = (index, campo, valor) => {
    setArticulos((prev) => prev.map((item, i) => (i === index ? { ...item, [campo]: valor } : item)));
  };

  const actualizarParrafo = (indexArticulo, indexParrafo, valor) => {
    setArticulos((prev) =>
      prev.map((item, i) => {
        if (i !== indexArticulo) return item;
        const parrafos = [...(item.parrafos || [''])];
        parrafos[indexParrafo] = valor;
        return { ...item, parrafos };
      })
    );
  };

  const agregarParrafo = (indexArticulo) => {
    setArticulos((prev) =>
      prev.map((item, i) => {
        if (i !== indexArticulo) return item;
        return { ...item, parrafos: [...(item.parrafos || ['']), ''] };
      })
    );
    const nuevoIndex = (articulos[indexArticulo]?.parrafos || ['']).length;
    setParrafoActivo(nuevoIndex);
    setArticuloActivo(indexArticulo);
    setCampoActivo(indexArticulo);
  };

  const eliminarParrafo = (indexArticulo, indexParrafo) => {
    setArticulos((prev) =>
      prev.map((item, i) => {
        if (i !== indexArticulo) return item;
        const parrafos = [...(item.parrafos || [''])];
        if (parrafos.length <= 1) return item;
        parrafos.splice(indexParrafo, 1);
        return { ...item, parrafos };
      })
    );
    setParrafoActivo((prev) => (prev >= indexParrafo && prev > 0 ? prev - 1 : prev));
  };

  const claveParrafoRef = (indexArticulo, indexParrafo) => `${indexArticulo}-${indexParrafo}`;

  const obtenerTextareaParrafoActivo = () => {
    if (campoActivo === 'introduccion') return null;
    const indexArticulo = typeof campoActivo === 'number' ? campoActivo : articuloActivo;
    return parrafoTextareaRefs.current[claveParrafoRef(indexArticulo, parrafoActivo)] || null;
  };

  const agregarArticulo = () => {
    setArticulos((prev) => [...prev, articuloVacio()]);
    const nuevoIndex = articulos.length;
    setArticuloActivo(nuevoIndex);
    setCampoActivo(nuevoIndex);
  };

  const eliminarArticulo = (index) => {
    if (articulos.length === 1) {
      setArticulos([articuloVacio()]);
      setArticuloActivo(0);
      return;
    }

    setArticulos((prev) => prev.filter((_, i) => i !== index));
    setArticuloActivo((prev) => (prev >= index && prev > 0 ? prev - 1 : prev));
  };

  const moverArticulo = (index, direccion) => {
    const nuevoIndex = index + direccion;
    if (nuevoIndex < 0 || nuevoIndex >= articulos.length) return;

    setArticulos((prev) => {
      const copia = [...prev];
      const [item] = copia.splice(index, 1);
      copia.splice(nuevoIndex, 0, item);
      return copia;
    });
    setArticuloActivo(nuevoIndex);
  };

  const aplicarNegrita = () => {
    if (campoActivo === 'introduccion') {
      aplicarNegritaEnTextarea(introTextareaRef.current, setIntroduccion);
      return;
    }

    const indexArticulo = typeof campoActivo === 'number' ? campoActivo : articuloActivo;
    const textarea = obtenerTextareaParrafoActivo();
    aplicarNegritaEnTextarea(textarea, (valor) => actualizarParrafo(indexArticulo, parrafoActivo, valor));
  };

  const insertarVariable = (variableKey) => {
    const token = `{{${variableKey}}}`;

    if (campoActivo === 'introduccion') {
      const textarea = introTextareaRef.current;
      setIntroduccion((prev) => {
        const contenido = prev || '';
        if (textarea) {
          const inicio = textarea.selectionStart ?? contenido.length;
          const fin = textarea.selectionEnd ?? contenido.length;
          const nuevoContenido = contenido.slice(0, inicio) + token + contenido.slice(fin);
          requestAnimationFrame(() => {
            textarea.focus();
            const posicion = inicio + token.length;
            textarea.setSelectionRange(posicion, posicion);
          });
          return nuevoContenido;
        }
        return contenido + token;
      });
      return;
    }

    const indexArticulo = articuloActivo;
    const textarea = obtenerTextareaParrafoActivo();

    setArticulos((prev) => {
      const copia = [...prev];
      const actual = copia[indexArticulo] || articuloVacio();
      const parrafos = [...(actual.parrafos || [''])];
      const contenido = parrafos[parrafoActivo] || '';

      if (textarea) {
        const inicio = textarea.selectionStart ?? contenido.length;
        const fin = textarea.selectionEnd ?? contenido.length;
        parrafos[parrafoActivo] = contenido.slice(0, inicio) + token + contenido.slice(fin);
        copia[indexArticulo] = { ...actual, parrafos };

        requestAnimationFrame(() => {
          textarea.focus();
          const posicion = inicio + token.length;
          textarea.setSelectionRange(posicion, posicion);
        });
      } else {
        parrafos[parrafoActivo] = contenido + token;
        copia[indexArticulo] = { ...actual, parrafos };
      }

      return copia;
    });
  };

  const handleGuardar = async () => {
    if (!empresaId) {
      setError('No hay empresa seleccionada');
      return;
    }

    if (!tipo.trim()) {
      setError('Selecciona el tipo de contrato');
      return;
    }

    if (!titulo.trim()) {
      setError('Ingresa el título de la plantilla');
      return;
    }

    if (!introduccion.trim()) {
      setError('Ingresa la introducción del contrato (comparecencia de las partes antes del artículo 1)');
      return;
    }

    const articulosValidos = articulos
      .filter(articuloTieneContenido)
      .map((item) => {
        const api = articuloParaApi({
          titulo: tituloSinNegritaEnvolvente(normalizarMarcadoresNegrita(item.titulo || '')),
          parrafos: (item.parrafos || []).map((p) => normalizarMarcadoresNegrita(p)),
        });
        return {
          ...api,
          contenido: normalizarMarcadoresNegrita(api.contenido || ''),
        };
      });

    if (articulosValidos.length === 0) {
      setError('Agrega al menos un artículo con título o contenido');
      return;
    }

    setGuardando(true);
    setError('');

    const payload = {
      empresa_id: empresaId,
      tipo: tipo.trim(),
      titulo: titulo.trim(),
      fuente: normalizarFuenteContrato(fuente),
      introduccion: normalizarMarcadoresNegrita(introduccion).trim(),
      articulos: articulosValidos,
      activo: true,
    };

    const response = plantilla?.id
      ? await plantillasContratoService.actualizarPlantilla(plantilla.id, payload)
      : await plantillasContratoService.crearPlantilla(payload);

    setGuardando(false);

    if (response.success) {
      onSuccess?.(response.message);
      onClose();
      return;
    }

    setError(formatearErrorPlantilla(response));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-[96rem] max-h-[94vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {plantilla ? 'Editar plantilla' : 'Nueva plantilla de contrato'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Redacta a la izquierda y revisa la vista previa en el centro. Las variables se muestran con datos de ejemplo.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-hidden grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_240px]">
          <div className="overflow-y-auto p-4 sm:p-6 space-y-5 border-b xl:border-b-0 xl:border-r border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de contrato</label>
                <select
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Seleccionar tipo</option>
                  {tiposContrato.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Título de la plantilla</label>
                <input
                  type="text"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  placeholder="Ej: Contrato determinado - Obra"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de letra</label>
                <select
                  value={fuente}
                  onChange={(e) => setFuente(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  style={{ fontFamily: FUENTES_CONTRATO.find((item) => item.id === fuente)?.stack }}
                >
                  {fuentesDisponibles.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Recomendado para contratos: {obtenerEtiquetaFuenteContrato(FUENTE_CONTRATO_DEFAULT)}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Introducción</label>
              <p className="text-xs text-gray-500 mb-2">
                Comparecencia de las partes antes del artículo 1. Usa variables para datos del empleador y trabajador.
                Negrita: <code className="text-indigo-600">**texto**</code>
              </p>
              <textarea
                ref={introTextareaRef}
                value={introduccion}
                onChange={(e) => setIntroduccion(e.target.value)}
                onFocus={() => setCampoActivo('introduccion')}
                rows={7}
                placeholder={INTRODUCCION_PLACEHOLDER}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm ${
                  campoActivo === 'introduccion' ? 'border-indigo-400 bg-indigo-50/40' : 'border-gray-300'
                }`}
                style={{ fontFamily: resolverStackFuenteContrato(fuente) }}
              />
            </div>

            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Artículos</h3>
              <button
                type="button"
                onClick={agregarArticulo}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                + Agregar artículo
              </button>
            </div>

            <div className="space-y-4">
              {articulos.map((articulo, index) => (
                <div
                  key={`articulo-${index}`}
                  className={`border rounded-lg p-4 ${articuloActivo === index && campoActivo !== 'introduccion' ? 'border-indigo-400 bg-indigo-50/40' : 'border-gray-200'}`}
                  onClick={() => {
                    setCampoActivo(index);
                    setArticuloActivo(index);
                  }}
                >
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <span className="text-sm font-medium text-gray-700">Artículo {index + 1}</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); moverArticulo(index, -1); }}
                        disabled={index === 0}
                        className="px-2 py-1 text-xs border rounded disabled:opacity-40"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); moverArticulo(index, 1); }}
                        disabled={index === articulos.length - 1}
                        className="px-2 py-1 text-xs border rounded disabled:opacity-40"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); eliminarArticulo(index); }}
                        className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>

                  <input
                    type="text"
                    value={articulo.titulo}
                    onChange={(e) => actualizarArticulo(index, 'titulo', e.target.value)}
                    placeholder="Ej: PRIMERA.- OBJETO DEL CONTRATO"
                    className="w-full px-3 py-2 mb-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />

                  <div className="space-y-3">
                    {(articulo.parrafos || ['']).map((parrafo, parrafoIndex) => (
                      <div key={`articulo-${index}-parrafo-${parrafoIndex}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-500">
                            Párrafo {parrafoIndex + 1}
                          </span>
                          {(articulo.parrafos || []).length > 1 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                eliminarParrafo(index, parrafoIndex);
                              }}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Quitar párrafo
                            </button>
                          )}
                        </div>
                        <textarea
                          ref={(el) => {
                            parrafoTextareaRefs.current[claveParrafoRef(index, parrafoIndex)] = el;
                          }}
                          value={parrafo}
                          onChange={(e) => actualizarParrafo(index, parrafoIndex, e.target.value)}
                          onFocus={() => {
                            setCampoActivo(index);
                            setArticuloActivo(index);
                            setParrafoActivo(parrafoIndex);
                          }}
                          rows={4}
                          placeholder="Redacta un párrafo. Usa párrafos separados para suavizar los saltos de página."
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm ${
                            articuloActivo === index && parrafoActivo === parrafoIndex && campoActivo !== 'introduccion'
                              ? 'border-indigo-400 bg-indigo-50/40'
                              : 'border-gray-300'
                          }`}
                          style={{ fontFamily: resolverStackFuenteContrato(fuente) }}
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        agregarParrafo(index);
                      }}
                      className="text-xs text-indigo-700 hover:text-indigo-900 font-medium"
                    >
                      + Agregar párrafo
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-y-auto overflow-x-hidden bg-slate-100 border-b xl:border-b-0 xl:border-r border-gray-200 min-w-0">
            <div className="sticky top-0 z-10 bg-slate-100 border-b border-gray-200 px-4 py-3">
              <h4 className="text-sm font-semibold text-gray-900">Vista previa del contrato</h4>
              <p className="text-xs text-gray-500 mt-0.5">
                Fuente: {obtenerEtiquetaFuenteContrato(fuente)} · Hojas A4 aproximadas
              </p>
            </div>
            <div className="p-4 flex justify-center min-w-0">
              <ContratoVistaPreviaPaginada
                htmlContenido={htmlContenidoVistaPrevia}
                logoSrc={logoEmpresaUrl}
                fontFamily={fontFamilyVistaPrevia}
              />
            </div>
          </div>

          <div className="bg-gray-50 overflow-y-auto p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">Variables disponibles</h4>
            <p className="text-xs text-gray-600 mb-3">
              Haz clic para insertar en la introducción o en el artículo activo. En la vista previa se muestran con datos de ejemplo.
            </p>
            <button
              type="button"
              onClick={aplicarNegrita}
              className="mb-4 w-full px-2 py-1.5 text-xs font-semibold bg-white border border-gray-300 text-gray-800 rounded hover:bg-gray-100"
              title="Envuelve el texto seleccionado con ** para negrita"
            >
              B Negrita (**texto**)
            </button>

            <div className="space-y-4">
              {Object.entries(variablesPorGrupo).map(([grupo, items]) => (
                <div key={grupo}>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{grupo}</p>
                  <div className="flex flex-wrap gap-2">
                    {items.map((variable) => (
                      <button
                        key={variable.key}
                        type="button"
                        onClick={() => insertarVariable(variable.key)}
                        className="px-2 py-1 text-xs bg-white border border-indigo-200 text-indigo-700 rounded hover:bg-indigo-50"
                        title={variable.key}
                      >
                        {variable.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="px-6 py-3 bg-red-50 border-t border-red-100 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleGuardar}
            disabled={guardando}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300"
          >
            {guardando ? 'Guardando...' : 'Guardar plantilla'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlantillaContratoModal;
